import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import useTitle from "@/hooks/useTitle";
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic";
import PulseLoader from "react-spinners/PulseLoader";
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { useGetChallengesQuery } from "../challenges/challengesApiSlice";
import { useCreateNewUserChallengeTeamEvalMutation } from "./userChallengeTeamEvalsApiSlice";

const EtAddOrUpdateUserChallengeTeamEvalComp = ({
    mode = 'create', 
    existingEvalData = null, 
    challengeId, 
    teamEvaluatorId, 
    teamEvaluatorOrgId, 
    userId, 
    userOrgId, 
    trailId, 
    sessionId, 

    // 20 Aug 2026 - Active cohort for this Team evaluation.
    cohortId,

    triggeringUrl, 
    onClose
}) => {
  // First Created - 20 Aug 2026
  // Author - Atul Pandey
  // Inherited from EtAddOrUpdateUserChallengeSponsorEvalComp
  // Purpose - to create new as well as update Team user challenge evals
  // Updated - 20 Aug 2026 - cohortId
  // Updated - 20 Aug 2026 - Multiple evaluation support


  // 0 - Set Page Title
  useTitle('Create New or Update Team Challenge Evaluation');
  
  // 1a- Set Page Navigation
  const navigate = useNavigate();

  // 1b - Extract incoming data:
  // console.log('Mode:', mode)
  // console.log('ExistingEvalData:', existingEvalData)
  // console.log('ChallengeId:', challengeId)
  // console.log('TeamEvaluatorId:', teamEvaluatorId)
  // console.log('TeamEvaluatorOrgId:', teamEvaluatorOrgId)
  // console.log('UserId:', userId)
  // console.log('UserOrgId:', userOrgId)
  // console.log('TrailId:', trailId)
  // console.log('SessionId:', sessionId)
  // console.log('TriggeringUrl:', triggeringUrl)
  // console.log('CohortId:', cohortId)


  // 2a - Set the mutation to create the new challenge
  const [
    createNewUserChallengeTeamEval,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    }
  ] = useCreateNewUserChallengeTeamEvalMutation();


  // 2b - Get ChallengesData
  const {
    data: challengesData
  } = useGetChallengesQuery()

  const challenges =
    challengesData?.ids?.map(
      (id) => {
        const challenge =
          challengesData?.entities[id]

        return challenge
      }
    )

  // console.log('challenges', challenges)


  // 2c - Filter the challengesData for the given challengdId
  const filteredChallenge =
    challenges?.find(
      (challenge) =>
        challenge._id === challengeId
    )

  // console.log('Filtered Challenge', filteredChallenge)


  // 2d - Extract all the challenge dimensions for the filtered challenge
  const challengeDimensions =
    filteredChallenge?.challengeDimensions

  // console.log('challenge dimensions:', challengeDimensions)


  // 2e - Extract all the challenge dim id data
  const challengeDimIds =
    challengeDimensions?.map(
      (dimension) => {
        const challengeDimId =
          dimension?.challengeDimId

        return challengeDimId
      }
    )

  // console.log('Challenge Dim Ids', challengeDimIds)
  // console.log(
  //   'Challenge Dim Ids',
  //   challengeDimIds?.map(
  //     dim => dim.challengeDimensionName
  //   )
  // )


  // 3a - State to keep track of selected challenge dimensions
  const [
    selectedChallengeDims,
    setSelectedChallengeDims
  ] = useState([]);

  // console.log(
  //   'Selected Challenge Dims',
  //   selectedChallengeDims
  // )


  // 3b - Create the form data object
  const [
    formData,
    setFormData
  ] = useState({
    challengeId: challengeId,
    teamEvaluatorId: teamEvaluatorId,
    teamEvaluatorOrgId: teamEvaluatorOrgId,
    userId: userId,
    userOrgId: userOrgId,
    trailId: trailId,
    sessionId: sessionId,

    // 20 Aug 2026 - Cohort required by Team evaluation controller.
    cohortId: cohortId,

    // 20 Aug 2026 - Multiple evaluation metadata.
    evaluationType: 'checkpoint',
    evaluationStatus: 'submitted',
    evaluationReason: '',
    evaluationChangeReason: '',

    userChallengeTeamEvals: [
      {
        userChallengeDimId: "",
        userChallengeDimTeamScore: "",
        userChallengeDimTeamEvalText: "",
      },
    ],
  });


  // 20 Aug 2026 - Normalize plain IDs and populated references.
  const getIdValue = (value) => {
    if (!value) {
      return ''
    }

    if (typeof value === 'string') {
      return value
    }

    return value?._id || ''
  }


  // 3c - Use Existing Evals data for existing eval records
  // 20 Aug 2026
  useEffect(() => {
    if (
      mode === 'update' &&
      existingEvalData
    ) {
      const normalizedEvaluations =
        existingEvalData
          .userChallengeTeamEvals
          ?.map(
            (evalItem) => ({
              ...evalItem,

              userChallengeDimId:
                getIdValue(
                  evalItem.userChallengeDimId
                ),

              userChallengeDimTeamScore:
                evalItem
                  .userChallengeDimTeamScore ??
                '',

              userChallengeDimTeamEvalText:
                evalItem
                  .userChallengeDimTeamEvalText ??
                '',
            })
          ) || []

      setFormData({
        challengeId:
          getIdValue(
            existingEvalData.challengeId
          ) || challengeId,

        teamEvaluatorId:
          getIdValue(
            existingEvalData.teamEvaluatorId
          ) || teamEvaluatorId,

        teamEvaluatorOrgId:
          getIdValue(
            existingEvalData.teamEvaluatorOrgId
          ) || teamEvaluatorOrgId,

        userId:
          getIdValue(
            existingEvalData.userId
          ) || userId,

        userOrgId:
          getIdValue(
            existingEvalData.userOrgId
          ) || userOrgId,

        trailId:
          getIdValue(
            existingEvalData.trailId
          ) || trailId,

        sessionId:
          getIdValue(
            existingEvalData.sessionId
          ) || sessionId,

        // Send the current active cohort for authorization.
        // The backend retains the evaluation's historical cohort.
        cohortId:
          cohortId ||
          getIdValue(
            existingEvalData.cohortId
          ),

        evaluationType:
          existingEvalData.evaluationType ||
          'checkpoint',

        evaluationStatus:
          existingEvalData.evaluationStatus ||
          'submitted',

        evaluationReason:
          existingEvalData.evaluationReason ||
          '',

        evaluationChangeReason: '',

        userChallengeTeamEvals:
          normalizedEvaluations,
      })

      setSelectedChallengeDims(
        normalizedEvaluations.map(
          (evalItem) =>
            evalItem.userChallengeDimId
        )
      )

      return
    }

    // Create mode starts a new evaluation instance.
    setFormData({
      challengeId,
      teamEvaluatorId,
      teamEvaluatorOrgId,
      userId,
      userOrgId,
      trailId,
      sessionId,
      cohortId,

      evaluationType: 'checkpoint',
      evaluationStatus: 'submitted',
      evaluationReason: '',
      evaluationChangeReason: '',

      userChallengeTeamEvals: [
        {
          userChallengeDimId: '',
          userChallengeDimTeamScore: '',
          userChallengeDimTeamEvalText: '',
        },
      ],
    })

    setSelectedChallengeDims([])
  }, [
    mode,
    existingEvalData,
    challengeId,
    teamEvaluatorId,
    teamEvaluatorOrgId,
    userId,
    userOrgId,
    trailId,
    sessionId,
    cohortId,
  ])


  // 3d - Filter out already selected challengeDimIds
  const availableChallengeDims =
    challengeDimIds?.filter(
      dim =>
        !selectedChallengeDims.includes(
          dim._id
        )
    );

  // console.log(
  //   'available challenge Dims',
  //   availableChallengeDims
  // )


  // 3e - Create options for select box from available challenge dimensions
  const challengeDimOptions =
    availableChallengeDims?.map(
      (dim) => (
        <option
          key={dim._id}
          value={dim._id}
        >
          {dim.challengeDimensionName}
        </option>
      )
    );


  // 4 - Reset form upon successful submission
  useEffect(() => {
    if (isSuccess) {
      setFormData({
        challengeId: "",
        teamEvaluatorId: "",
        teamEvaluatorOrgId: "",
        userId: "",
        userOrgId: "",
        trailId: "",
        sessionId: "",

        // 20 Aug 2026
        cohortId: "",

        challengeDimId: "",
        userChallengeTeamEvals: [{}],
      });

      if (onClose) {
        onClose();
      } else {
        navigate(
          triggeringUrl ||
          '/userChallengeTeamEvals'
        );
      }
    }
  }, [
    isSuccess,
    navigate,
    onClose,
    triggeringUrl
  ]);


  // 5a - 20 Aug 2026 - Form field updates / change handling.
  //
  // Do not mutate evaluation objects directly because existing evaluation data
  // may originate from RTK Query and be frozen in development mode.
  const handleChange = (e) => {
    const {
      name,
      value
    } = e.target

    const [
      fieldName,
      indexValue
    ] = name.split('-')

    const index =
      Number(indexValue)

    setFormData(
      (prevState) => ({
        ...prevState,

        userChallengeTeamEvals:
          prevState
            .userChallengeTeamEvals
            .map(
              (
                evaluationItem,
                evaluationIndex
              ) => {
                if (
                  evaluationIndex !== index
                ) {
                  return evaluationItem
                }

                return {
                  ...evaluationItem,

                  // 20 Aug 2026
                  [fieldName]:
                    fieldName ===
                    'userChallengeDimTeamScore'
                      ? value === ''
                        ? ''
                        : Number(value)
                      : value,
                }
              }
            ),
      })
    )

    // If a dimension is selected, update the selected dimensions state.
    if (
      fieldName ===
      'userChallengeDimId'
    ) {
      setSelectedChallengeDims(
        (prevState) => {
          const updatedDims =
            [...prevState]

          updatedDims[index] =
            value

          return updatedDims
        }
      )
    }
  }


  // 5b - 20 Aug 2026 - Handle Metadata change
  const handleMetadataChange =
    (e) => {
      const {
        name,
        value
      } = e.target

      setFormData(
        (prevState) => ({
          ...prevState,
          [name]: value,
        })
      )
    }


  // 5c - Add new challenge dimension evaluation field
  const handleAddEval = () => {
    setFormData(
      prevState => ({
        ...prevState,

        userChallengeTeamEvals: [
          ...prevState.userChallengeTeamEvals,
          {
            userChallengeDimId: "",
            userChallengeDimTeamScore: "",
            userChallengeDimTeamEvalText: "",
          }
        ]
      })
    );

    setSelectedChallengeDims(
      prevState => [
        ...prevState,
        ""
      ]
    );
  };


  // 5d - Remove challenge dimension evaluation field
  const handleRemoveEval = (index) => {
    setFormData(
      prevState => ({
        ...prevState,

        userChallengeTeamEvals:
          prevState
            .userChallengeTeamEvals
            .filter(
              (_, i) =>
                i !== index
            )
      })
    );

    setSelectedChallengeDims(
      prevState =>
        prevState.filter(
          (_, i) =>
            i !== index
        )
    );
  };


  // 6 - Form Submit Logic
  // 20 Aug 2026
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.cohortId) {
      alert(
        'Please select a cohort before saving the Team evaluation.'
      )
      return
    }

    if (!formData.teamEvaluatorId) {
      alert(
        'A Team evaluator is required before saving the Team evaluation.'
      )
      return
    }

    if (!formData.userId) {
      alert(
        'A user must be selected before saving the Team evaluation.'
      )
      return
    }

    const evaluationsToSubmit =
      formData
        .userChallengeTeamEvals
        .filter(
          (evalItem) =>
            evalItem.userChallengeDimId ||
            evalItem
              .userChallengeDimTeamScore !==
              '' ||
            evalItem
              .userChallengeDimTeamEvalText
              ?.trim()
        )

    if (
      evaluationsToSubmit.length === 0
    ) {
      alert(
        'Please evaluate at least one challenge dimension.'
      )
      return
    }

    const hasIncompleteStartedEvaluation =
      evaluationsToSubmit.some(
        (evalItem) =>
          !evalItem.userChallengeDimId ||
          evalItem
            .userChallengeDimTeamScore ===
            '' ||
          evalItem
            .userChallengeDimTeamScore ===
            undefined ||
          evalItem
            .userChallengeDimTeamScore ===
            null
      )

    if (
      hasIncompleteStartedEvaluation
    ) {
      alert(
        'Each selected dimension must include a score.'
      )
      return
    }

    const userChallengeTeamEvalData = {
      challengeId:
        formData.challengeId,

      teamEvaluatorId:
        formData.teamEvaluatorId,

      teamEvaluatorOrgId:
        formData.teamEvaluatorOrgId,

      userId:
        formData.userId,

      userOrgId:
        formData.userOrgId,

      trailId:
        formData.trailId,

      sessionId:
        formData.sessionId,

      cohortId:
        formData.cohortId,

      evaluationType:
        formData.evaluationType,

      evaluationStatus:
        formData.evaluationStatus,

      evaluationReason:
        formData
          .evaluationReason
          ?.trim() ||
        undefined,

      evaluationChangeReason:
        mode === 'update'
          ? formData
              .evaluationChangeReason
              ?.trim() ||
            undefined
          : undefined,

      userChallengeTeamEvals:
        evaluationsToSubmit.map(
          (evalItem) => ({
            ...evalItem,

            userChallengeDimTeamScore:
              Number(
                evalItem
                  .userChallengeDimTeamScore
              ),
          })
        ),

      ...(
        mode === 'update' &&
        existingEvalData?._id
          ? {
              evaluationId:
                existingEvalData._id,
            }
          : {}
      ),
    }

    const submitEvaluation =
      async (payload) => {
        try {
          await createNewUserChallengeTeamEval(
            payload
          ).unwrap()
        } catch (submitError) {
          console.error(
            submitError
          )
        }
      }

    if (formData.pic) {
      const reader =
        new FileReader()

      reader.onloadend =
        async () => {
          await submitEvaluation({
            ...userChallengeTeamEvalData,
            pic: reader.result,
          })
        }

      reader.readAsDataURL(
        formData.pic
      )

      return
    }

    await submitEvaluation(
      userChallengeTeamEvalData
    )
  }


  // 7 - Handle Cancel
  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(
        triggeringUrl ||
        "/userChallengeTeamEvals"
      );
    }
  };


  // 8 - Handle Clear
  // 20 Aug 2026
  const handleClear = () => {
    if (
      mode === 'update' &&
      existingEvalData
    ) {
      const normalizedEvaluations =
        existingEvalData
          .userChallengeTeamEvals
          ?.map(
            (evalItem) => ({
              ...evalItem,

              userChallengeDimId:
                getIdValue(
                  evalItem.userChallengeDimId
                ),

              userChallengeDimTeamScore:
                evalItem
                  .userChallengeDimTeamScore ??
                '',

              userChallengeDimTeamEvalText:
                evalItem
                  .userChallengeDimTeamEvalText ??
                '',
            })
          ) || []

      setFormData({
        challengeId:
          getIdValue(
            existingEvalData.challengeId
          ) || challengeId,

        teamEvaluatorId:
          getIdValue(
            existingEvalData.teamEvaluatorId
          ) || teamEvaluatorId,

        teamEvaluatorOrgId:
          getIdValue(
            existingEvalData.teamEvaluatorOrgId
          ) || teamEvaluatorOrgId,

        userId:
          getIdValue(
            existingEvalData.userId
          ) || userId,

        userOrgId:
          getIdValue(
            existingEvalData.userOrgId
          ) || userOrgId,

        trailId:
          getIdValue(
            existingEvalData.trailId
          ) || trailId,

        sessionId:
          getIdValue(
            existingEvalData.sessionId
          ) || sessionId,

        cohortId:
          cohortId ||
          getIdValue(
            existingEvalData.cohortId
          ),

        evaluationType:
          existingEvalData.evaluationType ||
          'checkpoint',

        evaluationStatus:
          existingEvalData.evaluationStatus ||
          'submitted',

        evaluationReason:
          existingEvalData.evaluationReason ||
          '',

        evaluationChangeReason: '',

        userChallengeTeamEvals:
          normalizedEvaluations,
      })

      setSelectedChallengeDims(
        normalizedEvaluations.map(
          (evalItem) =>
            evalItem.userChallengeDimId
        )
      )

      return
    }

    setFormData({
      challengeId,
      teamEvaluatorId,
      teamEvaluatorOrgId,
      userId,
      userOrgId,
      trailId,
      sessionId,
      cohortId,

      evaluationType: 'checkpoint',
      evaluationStatus: 'submitted',
      evaluationReason: '',
      evaluationChangeReason: '',

      userChallengeTeamEvals: [
        {
          userChallengeDimId: '',
          userChallengeDimTeamScore: '',
          userChallengeDimTeamEvalText: '',
        },
      ],
    })

    setSelectedChallengeDims([])
  }


  return (
    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black scrollbar overflow-auto max-h-[60vh]">

      {isLoading && (
        <PulseLoader
          color={"#ea580c"}
        />
      )}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">

        {/* 20 Aug 2026 */}
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          {mode === 'update'
            ? `Update Team Evaluation ${
                existingEvalData
                  ?.evaluationNumber
                  ? `#${existingEvalData.evaluationNumber}`
                  : ''
              }`
            : 'Create New Team Evaluation'}
        </p>


        <div className="bg-gray-200 rounded m-1 p-1">

          <form
            onSubmit={handleSubmit}
            className="m-1 p-1 text-xs font-poppins"
          >

            {/* 20 Aug 2026 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">

              <select
                name="evaluationType"
                value={
                  formData.evaluationType
                }
                onChange={
                  handleMetadataChange
                }
                className="w-full rounded p-2"
              >
                <option value="baseline">
                  Baseline
                </option>

                <option value="checkpoint">
                  Checkpoint
                </option>

                <option value="final">
                  Final
                </option>

                <option value="ad_hoc">
                  Ad Hoc
                </option>
              </select>


              <select
                name="evaluationStatus"
                value={
                  formData.evaluationStatus
                }
                onChange={
                  handleMetadataChange
                }
                className="w-full rounded p-2"
              >
                <option value="submitted">
                  Submitted
                </option>

                <option value="draft">
                  Draft
                </option>
              </select>

            </div>


            <textarea
              name="evaluationReason"
              value={
                formData.evaluationReason
              }
              onChange={
                handleMetadataChange
              }
              placeholder="Reason or context for this evaluation"
              className="w-full rounded p-2 mb-3"
            />


            {mode === 'update' && (
              <textarea
                name="evaluationChangeReason"
                value={
                  formData
                    .evaluationChangeReason
                }
                onChange={
                  handleMetadataChange
                }
                placeholder="Reason for correcting this evaluation"
                className="w-full rounded p-2 mb-3"
              />
            )}


            {/* Challenge Team Evaluation Scores */}

            {formData
              .userChallengeTeamEvals
              ?.map(
                (
                  evalItem,
                  index
                ) => (
                  <div
                    key={index}
                    className="mb-4"
                  >

                    {/* Select for Challenge Dimension */}
                    <div className="flex justify-start items-center mb-2">

                      <select
                        name={`userChallengeDimId-${index}`}
                        value={
                          evalItem
                            .userChallengeDimId
                        }
                        onChange={
                          handleChange
                        }
                        className="w-full rounded p-2 m-1"
                      >
                        <option
                          value=""
                          disabled
                        >
                          Select Challenge Dimension
                        </option>


                        {availableChallengeDims
                          ?.map(
                            dim => (
                              <option
                                key={dim._id}
                                value={dim._id}
                              >
                                {
                                  dim
                                    .challengeDimensionName
                                }
                              </option>
                            )
                          )}


                        {/* Include the currently selected dimension in the options */}
                        {evalItem
                          ?.userChallengeDimId &&
                        !availableChallengeDims
                          ?.some(
                            dim =>
                              dim._id ===
                              evalItem
                                ?.userChallengeDimId
                          ) && (
                          <option
                            value={
                              evalItem
                                ?.userChallengeDimId
                            }
                          >
                            {
                              challengeDimIds
                                ?.find(
                                  dim =>
                                    dim._id ===
                                    evalItem
                                      ?.userChallengeDimId
                                )
                                ?.challengeDimensionName ||
                              "Selected Dimension"
                            }
                          </option>
                        )}

                      </select>

                    </div>


                    {/* Input for Team Evaluation Score */}
                    <div className="flex justify-start items-center mb-2">

                      <input
                        type="number"
                        name={`userChallengeDimTeamScore-${index}`}
                        placeholder="Enter Team Evaluation Score"
                        value={
                          evalItem
                            .userChallengeDimTeamScore
                        }
                        onChange={
                          handleChange
                        }
                        className="w-full rounded p-2 m-1"
                      />

                    </div>


                    {/* Textarea for Team Evaluation Text */}
                    <div className="flex justify-start items-center mb-2">

                      <textarea
                        name={`userChallengeDimTeamEvalText-${index}`}
                        placeholder="Enter Team Evaluation Text"
                        value={
                          evalItem
                            .userChallengeDimTeamEvalText
                        }
                        onChange={
                          handleChange
                        }
                        className="w-full rounded p-2 m-1"
                      />

                    </div>


                    {/* Remove Button */}
                    <div className="flex justify-end">

                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveEval(
                            index
                          )
                        }
                        className="flex justify-center items-center bg-red-500 text-white rounded p-1 m-1"
                      >
                        <MinusIcon className="h-5 w-5" />
                      </button>

                    </div>

                  </div>
                )
              )}


            {availableChallengeDims
              ?.length > 0 && (
              <div className="flex justify-start items-center m-1">

                <button
                  type="button"
                  onClick={
                    handleAddEval
                  }
                  className="flex justify-center items-center bg-green-500 text-white rounded p-1 m-1"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>

              </div>
            )}


            <div
              id="actions"
              className="bg-white m-1 px-1 py-3 rounded"
            >

              <EtBtnFormBasic
                name={
                  mode === 'create'
                    ? "Create"
                    : "Update"
                }
                type={'create'}
                handleClick={
                  handleSubmit
                }
              />

              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={
                  handleClear
                }
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={
                  handleCancel
                }
              />

            </div>

          </form>

        </div>

      </div>

    </div>
  )
}

export default EtAddOrUpdateUserChallengeTeamEvalComp